import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export type EngagementCallback<T = any> = (payload: T) => void;

class RealtimeEngagementService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();

  // Subscribe to post engagement updates (likes, comments, saves)
  subscribeToPostEngagement(
    postId: string,
    onLikeUpdate: EngagementCallback,
    onCommentUpdate: EngagementCallback,
    onSaveUpdate: EngagementCallback
  ): () => void {
    const channelName = `post-engagement:${postId}`;
    
    // Remove existing channel if it exists
    this.unsubscribeFromChannel(channelName);

    console.log(`🔄 Subscribing to post engagement: ${postId}`);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'like_added' }, (payload) => {
        console.log('❤️ Like added broadcast received:', payload);
        onLikeUpdate({ type: 'added', ...payload.payload });
      })
      .on('broadcast', { event: 'like_removed' }, (payload) => {
        console.log('💔 Like removed broadcast received:', payload);
        onLikeUpdate({ type: 'removed', ...payload.payload });
      })
      .on('broadcast', { event: 'comment_added' }, (payload) => {
        console.log('💬 Comment added broadcast received:', payload);
        onCommentUpdate({ type: 'added', ...payload.payload });
      })
      .on('broadcast', { event: 'comment_removed' }, (payload) => {
        console.log('🗑️ Comment removed broadcast received:', payload);
        onCommentUpdate({ type: 'removed', ...payload.payload });
      })
      .on('broadcast', { event: 'save_added' }, (payload) => {
        console.log('🔖 Save added broadcast received:', payload);
        onSaveUpdate({ type: 'added', ...payload.payload });
      })
      .on('broadcast', { event: 'save_removed' }, (payload) => {
        console.log('📌 Save removed broadcast received:', payload);
        onSaveUpdate({ type: 'removed', ...payload.payload });
      })
      .subscribe((status) => {
        console.log(`📡 Post engagement ${postId} subscription status:`, status);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to post engagement broadcasts');
          // Start polling for missed engagement as backup
          this.startEngagementPolling(postId, onLikeUpdate, onCommentUpdate);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        } else if (status === 'TIMED_OUT') {
          console.error('⏰ Channel subscription timed out');
        }
      });

    this.channels.set(channelName, channel);

    // Return unsubscribe function
    return () => {
      this.unsubscribeFromChannel(channelName);
      this.stopEngagementPolling(postId);
    };
  }

  // Start polling for engagement updates as backup
  private startEngagementPolling(
    postId: string, 
    onLikeUpdate: EngagementCallback,
    onCommentUpdate: EngagementCallback
  ): void {
    const pollKey = `engagement-poll:${postId}`;
    
    // Clear existing polling
    this.stopEngagementPolling(postId);
    
    let lastLikeCount = 0;
    let lastCommentCount = 0;
    let isFirstPoll = true;
    
    const pollInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select(`
            id,
            post_likes (id),
            post_comments (id)
          `)
          .eq('id', postId)
          .maybeSingle();

        if (error) {
          console.error('❌ Engagement polling error:', error);
          return;
        }

        // If data is null, the post no longer exists
        if (!data) {
          console.log(`⚠️ Post ${postId} no longer exists, stopping polling`);
          this.stopEngagementPolling(postId);
          return;
        }

        const currentLikeCount = data.post_likes?.length || 0;
        const currentCommentCount = data.post_comments?.length || 0;

        // Skip first poll to establish baseline
        if (!isFirstPoll) {
          if (currentLikeCount !== lastLikeCount) {
            console.log(`📊 Polling detected like count change: ${lastLikeCount} → ${currentLikeCount}`);
            onLikeUpdate({ 
              type: 'count_changed', 
              post_id: postId, 
              new_count: currentLikeCount,
              old_count: lastLikeCount
            });
          }

          if (currentCommentCount !== lastCommentCount) {
            console.log(`📊 Polling detected comment count change: ${lastCommentCount} → ${currentCommentCount}`);
            onCommentUpdate({ 
              type: 'count_changed', 
              post_id: postId, 
              new_count: currentCommentCount,
              old_count: lastCommentCount
            });
          }
        }

        lastLikeCount = currentLikeCount;
        lastCommentCount = currentCommentCount;
        isFirstPoll = false;
      } catch (error) {
        console.error('❌ Engagement polling error:', error);
      }
    }, 3000); // Poll every 3 seconds

    this.pollingIntervals.set(pollKey, pollInterval);
    console.log(`🔄 Started engagement polling for post: ${postId}`);
  }

  private stopEngagementPolling(postId: string): void {
    const pollKey = `engagement-poll:${postId}`;
    const interval = this.pollingIntervals.get(pollKey);
    
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(pollKey);
      console.log(`⏹️ Stopped engagement polling for post: ${postId}`);
    }
  }

  // Broadcast like action to other users
  async broadcastLikeAction(
    postId: string, 
    userId: string, 
    action: 'added' | 'removed',
    newCount: number
  ): Promise<void> {
    const channelName = `post-engagement:${postId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`📤 Broadcasting like ${action}:`, { postId, userId, newCount });
      await channel.send({
        type: 'broadcast',
        event: `like_${action}`,
        payload: {
          post_id: postId,
          user_id: userId,
          new_count: newCount,
          timestamp: new Date().toISOString()
        }
      });
    } else {
      console.warn('⚠️ No channel found for broadcasting like action');
    }
  }

  // Broadcast comment action to other users
  async broadcastCommentAction(
    postId: string, 
    userId: string, 
    action: 'added' | 'removed',
    newCount: number,
    commentId?: string
  ): Promise<void> {
    const channelName = `post-engagement:${postId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`📤 Broadcasting comment ${action}:`, { postId, userId, newCount });
      await channel.send({
        type: 'broadcast',
        event: `comment_${action}`,
        payload: {
          post_id: postId,
          user_id: userId,
          comment_id: commentId,
          new_count: newCount,
          timestamp: new Date().toISOString()
        }
      });
    } else {
      console.warn('⚠️ No channel found for broadcasting comment action');
    }
  }

  // Broadcast save action to other users
  async broadcastSaveAction(
    postId: string, 
    userId: string, 
    action: 'added' | 'removed'
  ): Promise<void> {
    const channelName = `post-engagement:${postId}`;
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`📤 Broadcasting save ${action}:`, { postId, userId });
      await channel.send({
        type: 'broadcast',
        event: `save_${action}`,
        payload: {
          post_id: postId,
          user_id: userId,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Subscribe to global feed updates (for home page)
  subscribeToFeedUpdates(
    onPostUpdate: EngagementCallback
  ): () => void {
    const channelName = 'feed-updates';
    
    this.unsubscribeFromChannel(channelName);

    console.log('🔄 Subscribing to feed updates');

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'post_engagement_changed' }, (payload) => {
        console.log('📊 Feed engagement update received:', payload);
        onPostUpdate(payload.payload);
      })
      .subscribe((status) => {
        console.log(`📡 Feed updates subscription status:`, status);
      });

    this.channels.set(channelName, channel);

    return () => this.unsubscribeFromChannel(channelName);
  }

  // Broadcast to feed that a post's engagement changed
  async broadcastToFeed(
    postId: string,
    engagementType: 'like' | 'comment' | 'save',
    action: 'added' | 'removed',
    newCount?: number
  ): Promise<void> {
    const channelName = 'feed-updates';
    const channel = this.channels.get(channelName);
    
    if (channel) {
      console.log(`📤 Broadcasting to feed:`, { postId, engagementType, action, newCount });
      await channel.send({
        type: 'broadcast',
        event: 'post_engagement_changed',
        payload: {
          post_id: postId,
          engagement_type: engagementType,
          action,
          new_count: newCount,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Unsubscribe from a specific channel
  private unsubscribeFromChannel(channelName: string): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      console.log(`🔌 Unsubscribing from channel: ${channelName}`);
      supabase.removeChannel(channel);
      this.channels.delete(channelName);
    }
  }

  // Unsubscribe from all channels
  unsubscribeAll(): void {
    console.log('🔌 Unsubscribing from all engagement channels...');
    this.channels.forEach((channel, channelName) => {
      console.log(`🔌 Removing channel: ${channelName}`);
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    
    // Stop all polling
    this.pollingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.pollingIntervals.clear();
  }

  // Get active channels
  getActiveChannels(): string[] {
    return Array.from(this.channels.keys());
  }
}

export const realtimeEngagementService = new RealtimeEngagementService();